import { capitalize } from 'filters/text';
import parsePhoneNumber from 'libphonenumber-js';

const formatPhoneNumberEnum = Object.freeze({
    NATIONAL: 'national',
    INTERNATIONAL: 'international',
});
export default {
    shortName: (value) => {
        if (typeof value === 'string') {
            const splitName = value.split(' ');
            splitName[splitName.length - 1] = splitName[splitName.length - 1].substring(0, 1);
            return splitName.join(' ') + '.';
        } else {
            return value;
        }
    },
    upperCase: (value) => {
        if (!value) return '';
        return value.toString().toUpperCase();
    },
    lowerCase: (value) => {
        if (!value) return '';
        return value.toString().toLowerCase();
    },
    shortText: (value, size) => {
        if (typeof value === 'string' && size != undefined) {
            if (size == 'big' && value.length > 256)
                return value.substring(0, value.lastIndexOf(' ', 256)) + '...';
            else return value;
        } else {
            if (typeof value === 'string' && value.length > 88) {
                return value.substring(0, value.lastIndexOf(' ', 88)) + '...';
            } else {
                return value;
            }
        }
    },
    stripTags: (value) => {
        return value.replace(/<\/?[^>]+(>|$)/g, '');
    },
    dateFormatter(timestamp, format, showRelativeDate = true) {
        const lang = window.App.lang;
        const dateformat = window.sellsyapp?.$store?.state.corp.timeformat;
        const time24 = window.sellsyapp?.$store?.state.corp.hourformat == '24';
        const timezone = window.sellsyapp?.$store?.state.user.timezone;

        moment().locale(lang);
        moment.tz.setDefault(timezone);
        let momentDate = moment.unix(timestamp);
        if (!momentDate.isValid()) {
            momentDate = moment(timestamp);
        }

        const time = time24 ? 'HH:mm' : 'hh:mm a';
        const timeSec = time24 ? 'HH:mm:ss' : 'hh:mm:ss a';
        const mouth = 'MMMM';
        const shortMouth = 'MMM';
        const shortDate =
            dateformat == 'dmy' ? 'D ' + shortMouth + ' YYYY' : shortMouth + ' D, YYYY';
        const date = dateformat == 'dmy' ? 'D ' + mouth + ' YYYY' : mouth + ' D, YYYY';
        const slashedDate = dateformat == 'dmy' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
        const dashedDate = dateformat == 'dmy' ? 'DD-MM-YYYY' : 'MM-DD-YYYY';
        const today = window.i18n.gettext('Aujourd’hui');
        const tomorrow = window.i18n.gettext('Demain');
        const yesterday = window.i18n.gettext('Hier');

        const formatDate = function (momentDate, formatDate, formatTime, showRelativeDate) {
            if (formatTime) {
                formatTime = ' ' + formatTime;
            } else {
                formatTime = '';
            }

            if (!showRelativeDate) {
                var result = momentDate.format(formatDate + formatTime);
            } else {
                var result = momentDate.calendar(null, {
                    sameDay: '[' + today + ']' + formatTime,
                    nextDay: '[' + tomorrow + ']' + formatTime,
                    nextWeek: formatDate + formatTime,
                    lastDay: '[' + yesterday + ']' + formatTime,
                    lastWeek: formatDate + formatTime,
                    sameElse: formatDate + formatTime,
                });
            }

            if (!time24) {
                result = result.replace(/(pm|am)/i, (l) => l.toLowerCase());
            }

            return result;
        };

        switch (format) {
            case 'TIME':
                return formatDate(momentDate, time, false, false);
            case 'TIME_SEC':
                return formatDate(momentDate, timeSec, false, false);
            case 'SHORT_MONTH':
                return formatDate(momentDate, shortMouth + ' YYYY', false, false);
            case 'NORMAL_MONTH':
                return formatDate(momentDate, mouth + ' YYYY', false, false);
            case 'SLASHED_DATE':
                return formatDate(momentDate, slashedDate, false, showRelativeDate);
            case 'DASHED_DATE':
                return formatDate(momentDate, dashedDate, false, showRelativeDate);
            case 'SHORT_DATE':
                return formatDate(momentDate, shortDate, false, showRelativeDate);
            case 'NORMAL_DATE':
                return formatDate(momentDate, date, false, showRelativeDate);
            case 'NORMAL_DATE_WITH_DAY':
                return formatDate(momentDate, 'dddd ' + date, false, showRelativeDate);
            case 'SLASHED_DATE_TIME':
                return formatDate(momentDate, slashedDate, time, showRelativeDate);
            case 'SHORT_DATE_TIME':
                return formatDate(momentDate, shortDate, time, showRelativeDate);
            case 'NORMAL_DATE_TIME':
                return formatDate(momentDate, date, time, showRelativeDate);
            case 'NORMAL_DATE_TIME_WITH_DAY':
                return formatDate(momentDate, 'dddd ' + date, time, showRelativeDate);
            case 'SLASHED_DATE_TIME_SEC':
                return formatDate(momentDate, slashedDate, timeSec, showRelativeDate);
            case 'DASHED_DATE_TIME_SEC':
                return formatDate(momentDate, dashedDate, timeSec, showRelativeDate);
            case 'SHORT_DATE_TIME_SEC':
                return formatDate(momentDate, shortDate, timeSec, showRelativeDate);
            case 'NORMAL_DATE_TIME_SEC':
                return formatDate(momentDate, date, timeSec, showRelativeDate);
            default:
                throw new Error('Invalid date format: ' + format);
        }
    },
    dateIntervalFormatter(timestampStart, timestampEnd, format, showRelativeDate = true) {
        const dateStart = this.dateFormatter(timestampStart, format, showRelativeDate);
        const dateEnd = this.dateFormatter(timestampEnd, format, showRelativeDate);

        if (dateStart == dateEnd) {
            return [dateStart];
        }

        const matchStart = dateStart.match(/^(.+) ([0-9]{2}:.+)$/);
        const matchEnd = dateEnd.match(/^(.+) ([0-9]{2}:.+)$/);
        if (matchStart !== null && matchEnd !== null && matchStart[1] == matchEnd[1]) {
            return [dateStart, matchEnd[2], true];
        } else {
            return [dateStart, dateEnd, false];
        }
    },
    numberFormatWithOptions(value, forceOptions, withCurrency) {
        if (forceOptions === undefined) {
            forceOptions = {};
        }
        // override just for current method
        const numberFormat = Object.assign({}, window.sellsyapp?.$store?.state?.corp.numformat);
        if (Object.keys(forceOptions).length > 0) {
            Object.assign(numberFormat, forceOptions);
        }

        return numberTransformer(value, numberFormat, withCurrency);
    },
    numberFormat: (value, displayCurrency) => {
        return numberTransformer(
            value,
            window.sellsyapp?.$store?.state?.corp.numformat,
            displayCurrency,
        );
    },
    numberFormatCurrency: (value) => {
        return numberTransformer(value, window.sellsyapp?.$store?.state?.corp.numformat, true);
    },
    numberFormatCurrencyCustomNumberFormat: (value, numberFormat) => {
        return numberTransformer(
            value,
            { ...window.sellsyapp?.$store?.state?.corp.numformat, ...numberFormat },
            true,
        );
    },
    saasDateFormatted: (value) => {
        const date = moment(value);
        if (window.sellsyapp?.$store?.state.corp.timeformat === 'dmy') {
            return date.format('DD/MM/YYYY');
        }
        return date.format('MM/DD/YYYY');
    },
    creditCard: (lastNumbers, placeholder, nbChars) => {
        if (placeholder === undefined) {
            placeholder = 'x';
        }
        if (nbChars === undefined) {
            nbChars = 16;
        }

        return lastNumbers
            .padStart(nbChars, placeholder)
            .match(/.{1,4}/g)
            .join(' ');
    },
    floatNumber(value) {
        return value / 100;
    },
    numberFormatToFloatWithOptions(value, forceOptions, withCurrency) {
        const valueFloat = this.floatNumber(value);
        return this.numberFormatWithOptions(valueFloat, forceOptions, withCurrency);
    },
    fromNowCustom(datetime) {
        const lang = window.App.lang;
        const timezone = window.sellsyapp?.$store?.state.user.timezone;

        moment().locale(lang);
        moment.tz.setDefault(timezone);

        const momentDate = moment(datetime).utc(true);
        const momentDateUnix = momentDate.unix();
        const now = moment();
        const diffInMinutes = now.diff(momentDate, 'minutes');
        const todayStart = moment().startOf('day').unix();
        const todayEnd = moment().endOf('day').unix();
        const yesterdayStart = moment().subtract(1, 'days').startOf('day').unix();
        const yesterdayEnd = moment().subtract(1, 'days').endOf('day').unix();

        if (diffInMinutes < 1) {
            return window.i18n.__('Il y a quelque secondes');
        } else if (diffInMinutes < 59) {
            return window.i18n.n__(
                'Il y a environ %s minute',
                'Il y a environ %s minutes',
                parseInt(diffInMinutes),
                parseInt(diffInMinutes),
            );
        } else if (momentDateUnix >= todayStart && momentDateUnix <= todayEnd) {
            const diffInHours = now.diff(momentDate, 'hours');
            return window.i18n.n__(
                'Il y a %s heure',
                'Il y a %s heures',
                parseInt(diffInHours),
                parseInt(diffInHours),
            );
        } else if (momentDateUnix >= yesterdayStart && momentDateUnix <= yesterdayEnd) {
            return window.i18n.__('Hier - %(time)s', {
                time: momentDate.hours() + ':' + momentDate.minutes(),
            });
        } else {
            return momentDate.format('dddd DD MMMM - HH:mm');
        }
    },
    booleanFilter: (boolean) => {
        return boolean ? window.i18n.__('Oui') : window.i18n.__('Non');
    },
    formatPhoneNumber: (value, format = formatPhoneNumberEnum.NATIONAL) => {
        try {
            format = window.sellsyapp?.$store?.phoneFormat ?? format;
            const phoneNumber = parsePhoneNumber(value);
            return format === formatPhoneNumberEnum.NATIONAL
                ? phoneNumber.formatNational()
                : phoneNumber.formatInternational();
        } catch {
            return value;
        }
    },
    capitalize,
};

const numberTransformer = function (value, numberFormat, withCurrency) {
    // define default
    if (!value || value.NaN) value = 0;
    if (!!withCurrency) numberFormat.currency = numberFormat.currencysymbol;

    return !withCurrency
        ? NumberFormat.formatToDisplay(value, numberFormat)
        : NumberFormat.formatToDisplayWithCurrency(value, numberFormat);
};
